import { parseMarkdown } from "../markdown/parse.js";

/**
 * A markdown document as a list of top-level blocks plus the exact text
 * between them. Lossless by construction:
 *
 *   source === lead + blocks[0] + seps[0] + blocks[1] + … + tail
 *
 * Keeping `seps` (rather than re-joining with a canonical "\n\n") is what lets
 * the block editor do structural edits — split, merge, reorder — while still
 * leaving every untouched byte alone. It is `spliceFence`'s philosophy
 * (markdown/parseFence.ts) extended from one fence to the whole document, and
 * it's why the markdown mode can promise something the rich mode can't: an
 * edit rewrites only the block you edited.
 *
 * Blocks come from the renderer's own parse, so a `:::sidebyside` container is
 * one block, not three — you edit the row as a unit and it keeps rendering as
 * a grid when you leave it.
 */
export interface BlockDoc {
  lead: string;
  blocks: string[];
  /** seps[i] is the text between blocks[i] and blocks[i+1]; length n-1. */
  seps: string[];
  tail: string;
}

export function splitDoc(source: string): BlockDoc {
  const tree = parseMarkdown(source);
  const ranges: [number, number][] = [];
  for (const n of tree.children) {
    const s = n.position?.start.offset;
    const e = n.position?.end.offset;
    if (s == null || e == null) continue;
    ranges.push([s, e]);
  }
  if (ranges.length === 0) {
    return { lead: "", blocks: source ? [source] : [""], seps: [], tail: "" };
  }
  const blocks = ranges.map(([s, e]) => source.slice(s, e));
  const seps = ranges
    .slice(0, -1)
    .map(([, e], i) => source.slice(e, ranges[i + 1][0]));
  return {
    lead: source.slice(0, ranges[0][0]),
    blocks,
    seps,
    tail: source.slice(ranges[ranges.length - 1][1]),
  };
}

export function joinDoc(d: BlockDoc): string {
  let out = d.lead;
  d.blocks.forEach((b, i) => {
    out += b;
    if (i < d.blocks.length - 1) out += d.seps[i] ?? "\n\n";
  });
  return out + d.tail;
}

export function replaceBlock(d: BlockDoc, i: number, text: string): BlockDoc {
  return { ...d, blocks: d.blocks.map((b, j) => (j === i ? text : b)) };
}

/** Split block i at `offset` into two blocks, separated by a blank line. */
export function splitBlock(d: BlockDoc, i: number, offset: number): BlockDoc {
  const text = d.blocks[i];
  const blocks = [...d.blocks];
  blocks.splice(i, 1, text.slice(0, offset), text.slice(offset));
  const seps = [...d.seps];
  seps.splice(i, 0, "\n\n");
  return { ...d, blocks, seps };
}

/** Merge block i into block i-1; returns the doc and the join offset. */
export function mergeBack(
  d: BlockDoc,
  i: number,
): { doc: BlockDoc; offset: number } {
  if (i <= 0) return { doc: d, offset: 0 };
  const offset = d.blocks[i - 1].length;
  const blocks = [...d.blocks];
  blocks.splice(i - 1, 2, blocks[i - 1] + blocks[i]);
  const seps = [...d.seps];
  seps.splice(i - 1, 1);
  return { doc: { ...d, blocks, seps }, offset };
}

export function insertBlock(d: BlockDoc, i: number, text = ""): BlockDoc {
  const blocks = [...d.blocks];
  blocks.splice(i, 0, text);
  const seps = [...d.seps];
  seps.splice(Math.max(0, i - 1), 0, "\n\n");
  return { ...d, blocks, seps };
}

export function removeBlock(d: BlockDoc, i: number): BlockDoc {
  if (d.blocks.length <= 1) return { ...d, blocks: [""] };
  const blocks = [...d.blocks];
  blocks.splice(i, 1);
  const seps = [...d.seps];
  seps.splice(Math.min(i, seps.length - 1), 1);
  return { ...d, blocks, seps };
}

export function moveBlock(d: BlockDoc, i: number, dir: -1 | 1): BlockDoc {
  const j = i + dir;
  if (j < 0 || j >= d.blocks.length) return d;
  const blocks = [...d.blocks];
  [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
  return { ...d, blocks };
}

/**
 * Map an offset in a block's *rendered* text to an offset in its markdown, so
 * clicking a word puts the caret on that word rather than at the block start —
 * one of the two open questions left in docs/wiki/EditModes.md.
 *
 * Heuristic, not a parser: skip inline syntax characters and link targets, and
 * skip the line-start prefix (`#`, `>`, `- `, `1. `). Wrong by a character or
 * two in exotic cases; right in the ones people actually click.
 */
export function plainToRawOffset(raw: string, plainOffset: number): number {
  let i = 0;
  let p = 0;
  let atLineStart = true;
  while (i < raw.length && p < plainOffset) {
    if (atLineStart) {
      const m = /^(\s*(?:[#]{1,6}\s+|>\s?|[-*+]\s+|\d+\.\s+))/.exec(raw.slice(i));
      if (m) {
        i += m[1].length;
        atLineStart = false;
        continue;
      }
      atLineStart = false;
    }
    const c = raw[i];
    if (c === "\\") {
      i += 2;
      p += 1;
      continue;
    }
    if (c === "*" || c === "_" || c === "`" || c === "~" || c === "[") {
      i++;
      continue;
    }
    if (c === "]") {
      i++;
      if (raw[i] === "(") {
        const close = raw.indexOf(")", i);
        i = close === -1 ? raw.length : close + 1;
      }
      continue;
    }
    if (c === "\n") atLineStart = true;
    i++;
    p++;
  }
  return i;
}

/** Plain-text offset of a click inside a rendered block element. */
export function clickPlainOffset(el: HTMLElement, x: number, y: number): number {
  const doc = el.ownerDocument;
  let node: Node | null = null;
  let offset = 0;
  const anyDoc = doc as Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number,
    ) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  if (anyDoc.caretPositionFromPoint) {
    const pos = anyDoc.caretPositionFromPoint(x, y);
    if (pos) {
      node = pos.offsetNode;
      offset = pos.offset;
    }
  } else if (anyDoc.caretRangeFromPoint) {
    const r = anyDoc.caretRangeFromPoint(x, y);
    if (r) {
      node = r.startContainer;
      offset = r.startOffset;
    }
  }
  if (!node || !el.contains(node)) return 0;
  const range = doc.createRange();
  range.setStart(el, 0);
  range.setEnd(node, offset);
  return range.toString().length;
}
